import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { interval, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
    selector: 'app-delete-account-confirm-modal',
    standalone: false,
    template: `
        <div class="modal-header border-0 pb-0">
            <div class="d-flex align-items-center gap-2">
                <div class="danger-icon-wrapper">
                    <i class="ti ti-trash-x fs-3 text-danger"></i>
                </div>
                <h5 class="modal-title text-danger fw-bold mb-0">
                    Delete Account
                </h5>
            </div>
            <button type="button" class="btn-close" (click)="cancel()"></button>
        </div>

        <div class="modal-body pt-2">
            <!-- Big danger alert -->
            <div class="danger-banner mb-4">
                <div class="d-flex align-items-start gap-3">
                    <i class="ti ti-alert-octagon fs-1 text-danger flex-shrink-0 mt-1"></i>
                    <div>
                        <h6 class="fw-bold text-danger mb-1">Your clinic will be disabled immediately</h6>
                        <p class="mb-0 text-dark small">
                            From now on your clinic is not visible to patients, receives no new requests, your
                            subscriptions are cancelled and team members cannot sign in.
                        </p>
                        <p class="mt-2 mb-0 text-dark small">
                            After <strong>{{ graceDays }} days</strong> your account is <strong>permanently deleted</strong>,
                            including:
                        </p>
                        <ul class="mt-2 mb-0 small text-muted">
                            <li>Your clinic profile and all listings</li>
                            <li>All received quote and contact requests</li>
                            <li>Reviews and ratings</li>
                            <li>Subscriptions and wallet</li>
                            <li>Team members and their accounts</li>
                        </ul>
                        <p class="mt-2 mb-0 small text-muted">
                            Until then you can sign in with the owner account and cancel the deletion
                            (subscriptions are not restored). Invoices are kept for tax purposes.
                        </p>
                    </div>
                </div>
            </div>

            <!-- Countdown info -->
            <div class="d-flex align-items-center gap-2 mb-3" *ngIf="!canDelete">
                <div class="countdown-circle">{{ countdown }}</div>
                <span class="text-muted small">
                    Please read the above carefully. The confirm button will be enabled in
                    <strong>{{ countdown }} second{{ countdown !== 1 ? 's' : '' }}</strong>.
                </span>
            </div>

            <div class="alert alert-success d-flex align-items-center gap-2 mb-0 py-2" *ngIf="canDelete">
                <i class="ti ti-check fs-5"></i>
                <small>You may now confirm the deletion request.</small>
            </div>
        </div>

        <div class="modal-footer border-0 pt-0 gap-2">
            <button type="button" class="btn btn-outline-secondary" (click)="cancel()">
                <i class="ti ti-x me-1"></i> Cancel
            </button>
            <button
                type="button"
                class="btn btn-danger"
                [disabled]="!canDelete"
                (click)="confirm()">
                <i class="ti ti-trash me-1"></i>
                {{ canDelete ? 'Yes, schedule the deletion' : 'Delete account (' + countdown + ')' }}
            </button>
        </div>
    `,
    styles: [`
        .danger-icon-wrapper {
            width: 40px;
            height: 40px;
            border-radius: 8px;
            background: #fff1f0;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .danger-banner {
            background: #fff1f0;
            border: 1.5px solid #ffccc7;
            border-radius: 10px;
            padding: 16px;
        }

        .countdown-circle {
            min-width: 36px;
            height: 36px;
            border-radius: 50%;
            background: #e9ecef;
            border: 2px solid #adb5bd;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 700;
            font-size: 1rem;
            color: #495057;
            flex-shrink: 0;
            animation: pulse 1s ease-in-out infinite;
        }

        @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.12); }
        }
    `]
})
export class DeleteAccountConfirmModalComponent implements OnInit, OnDestroy {
    /** Days the clinic has to cancel the deletion (backend partner.deletion.grace-days). */
    @Input() graceDays = 90;
    countdown = 5;
    canDelete = false;
    private timerSub?: Subscription;

    constructor(public activeModal: NgbActiveModal) {}

    ngOnInit() {
        this.timerSub = interval(1000)
            .pipe(take(this.countdown))
            .subscribe(() => {
                this.countdown--;
                if (this.countdown <= 0) {
                    this.canDelete = true;
                }
            });
    }

    ngOnDestroy() {
        this.timerSub?.unsubscribe();
    }

    confirm() {
        this.activeModal.close('confirm');
    }

    cancel() {
        this.activeModal.dismiss('cancel');
    }
}

